export const THREE_COLORS = {
  terrain: 0x5f8367,
  terrainPuzzle: 0x5f8367,
  trail: 0xffffff,
  lake: '#4a90b8',
  // Neutral solid for marker/waypoint pieces in the RESULT viewer. preview.glb
  // carries no per-marker color source (per-marker colors live only in the build
  // config + Bambu slots), so markers render as a visible neutral solid rather
  // than borrowing terrain's vertex-color path (which would render them black).
  marker: 0x9aa0a6,
} as const

// Picker-facing defaults: hex strings that match THREE_COLORS above.
// Keep these in sync — the 3D viewer falls back to THREE_COLORS when a
// layer has no override, and the picker should display the same values.
export const DEFAULT_PREVIEW_COLORS = {
  terrain: '#5F8367',
  trail: '#FFFFFF',
  water: '#4A90B8',
} as const

export const HERO_LIGHTING = {
  primary: { intensity: 1.2, position: [2, 2, -1] as const },
  fill: { intensity: 0.9, position: [-1.5, 1, 1] as const },
  top: { intensity: 0.6, position: [0, 3, 0] as const },
  ambient: { intensity: 3.0 },
} as const

export const PREVIEW_LIGHTING = {
  primary: { intensity: 1.3, position: [2, 2, -1] as const },
  fill: { intensity: 0.7, position: [-1.5, 1, 1] as const },
  top: { intensity: 0.3, position: [0, 3, 0] as const },
  ambient: { intensity: 1.8 },
} as const

export const DARK_LIGHTING = {
  primary: { intensity: 1.3, position: [2, 1.5, -1] as const },
  fill: { intensity: 0.1, position: [-1.5, 1, 1] as const },
  top: { intensity: 0.3, position: [0, 3, 0] as const },
  ambient: { intensity: 1.3 },
} as const

export const ANIMATION = {
  pageTransition: 200,
  modalOpen: 300,
  modalClose: 200,
  buttonHover: 150,
  buttonPress: 100,
  cardEnter: 400,
  cardHover: 300,
  cardClick: 150,
  clusterZoom: 500,
  tooltipFade: 150,
  markerPulse: 300,
  viewTransitionShort: 800,
  viewTransitionLong: 1000,
  viewTransitionSequential: 1200,
} as const

export const BAND_REVEAL = {
  duration: 20,
  bandCount: 8,
  bandHeight: 0.4,
  stagger: 0.4,
  cameraViewScale: 0.55,
  contourInterval: 400,
} as const

export const PUZZLE_ASSEMBLY = {
  scrollVh: 400,
  scrollVhMobile: 300,
  dropProgressEnd: 0.85,
  seed: 42,
  pieceDuration: 0.12,
  cameraOrbitStart: -Math.PI / 4,
  cameraOrbitEnd: Math.PI / 4,
  layoutTransitionStart: 0.5,
  layoutTransitionEnd: 0.65,
  opacityFadePortion: 0.2,
} as const

export const SIZE_OPTIONS = Array.from({ length: 16 }, (_, i) => 125 + i * 25) // 125..500

// Kerf slider: total cut-gap width between puzzle pieces, 0.1–0.25mm in 0.025
// steps. At or below KERF_SEPARATE_THRESHOLD_MM the gap is too thin to print
// pieces assembled (they'd fuse), so by default the export modal locks the
// assembled layout off and selects the separated layout. That UI lock can be
// disabled via the FORCE_SEPARATE_THIN_KERF_ENABLED flag (lib/flags.ts),
// allowing assembled prints at any kerf. Mirrors backend/shared/constants.py.
export const KERF_MIN_MM = 0.1
export const KERF_MAX_MM = 0.25
export const KERF_STEP_MM = 0.025
export const KERF_DEFAULT_MM = 0.25
export const KERF_SEPARATE_THRESHOLD_MM = 0.15

// Z-scale (vertical exaggeration) slider for the live terrain preview (F07).
// Z_SCALE_MAX mirrors the upper clamp in shared/geo.py::calculate_responsive_z_scale
// (and its TS port in lib/zscale.ts). Z_SCALE_MIN is the SLIDER floor only — it sits
// intentionally BELOW the auto formula's 1.25 lower clamp so users can pull the
// relief down toward true scale; the auto suggestion itself never drops below 1.25
// (that clamp is hardcoded in lib/zscale.ts / geo.py, not derived from this). DEFAULT
// matches gen_terrain's safe fallback for an unknown elevation range.
export const Z_SCALE_MIN = 1.0
export const Z_SCALE_MAX = 15.0
export const Z_SCALE_STEP = 0.25
export const Z_SCALE_DEFAULT = 2.0

/** How far above the per-selection auto value the slider lets the user push the
 * exaggeration. The auto value already accounts for terrain relief (flat areas
 * suggest a higher value, steep areas a lower one), so 2× is an ample-but-not-
 * absurd ceiling — it keeps users off the full 15× unless the area's own auto
 * value is already high enough to warrant it. */
export const Z_SCALE_AUTO_HEADROOM = 2

/** Clamp to [Z_SCALE_MIN, max], snap to the slider step, and round away
 * binary-float artifacts so the displayed/emitted value is a clean multiple.
 * `max` defaults to the absolute Z_SCALE_MAX; pass `zScaleMaxFor(auto)` to apply
 * the per-selection cap. */
export function clampZScale(v: number, max: number = Z_SCALE_MAX): number {
  const stepped = Math.round(v / Z_SCALE_STEP) * Z_SCALE_STEP
  const clamped = Math.min(max, Math.max(Z_SCALE_MIN, stepped))
  return Math.round(clamped * 1000) / 1000
}

/** Effective slider max for a given per-selection auto value: `auto × headroom`,
 * snapped to step and capped at the absolute Z_SCALE_MAX. Falls back to the
 * absolute max when no auto value is known yet (null / non-finite / ≤ 0). */
export function zScaleMaxFor(auto: number | null | undefined): number {
  if (auto == null || !Number.isFinite(auto) || auto <= 0) return Z_SCALE_MAX
  return clampZScale(auto * Z_SCALE_AUTO_HEADROOM)
}

// Cluster grid tiers (server-side ST_SnapToGrid tiers, shared with the
// `*_clusters` materialized views). Used to pick the next zoom level when a
// cluster is clicked. Kept per-dataset so each can diverge later, but for now
// they share the same shape.
export const TRAIL_CLUSTER_TIERS = [3, 5, 7, 9, 11, 12] as const
export const LAKE_CLUSTER_TIERS = [3, 5, 7, 9, 11, 12] as const

export const ZOOM_TIER_MAP: Record<number, number> = {
  0: 3,
  1: 3,
  2: 3,
  3: 3,
  4: 3,
  5: 5,
  6: 5,
  7: 7,
  8: 7,
  9: 9,
  10: 9,
  11: 11,
  12: 12,
  13: 12,
  14: 14,
  15: 14,
  16: 14,
  17: 14,
  18: 14,
}

// Preset swatches for the color picker. Users can also enter any hex value.
export const COLOR_SWATCHES: Record<'terrain' | 'trail' | 'water', string[]> = {
  terrain: ['#5F8367', '#8B7355', '#D2B48C', '#a0402d', '#E5E5E5'],
  trail: ['#FFFFFF', '#FF5A1F', '#E6B800', '#C92A2A', '#000000'],
  water: ['#4A90B8', '#2E5C8A', '#0A2989', '#5FB8A8', '#B2D8D8'],
}

export const MAX_ELEVATION_SPLITS = 4

// ─── Satellite season ────────────────────────────────────────────────────────
// The user-facing time-of-year for satellite imagery. Each season maps
// hemisphere-aware to a CDSE quarterly mosaic (see lib/season.ts). Default =
// Summer, which resolves to the exact quarter the backend already defaults to
// (Q3 northern / Q1 southern), so an un-opted-in job keeps its existing cache key.
export type Season = 'winter' | 'spring' | 'summer' | 'fall'
export const SEASONS = [
  { id: 'spring', label: 'Spring' },
  { id: 'summer', label: 'Summer' },
  { id: 'fall', label: 'Fall' },
  { id: 'winter', label: 'Winter' },
] as const satisfies readonly { id: Season; label: string }[]
export const SEASON_DEFAULT: Season = 'summer'

// Satellite imagery YEAR. `'latest'` (the default) lets the pipeline pick the
// newest published mosaic; a number pins a specific year. The CDSE quarterly
// mosaics start in 2020 — a year below that hard-fails tile discovery, so the
// picker floors at SATELLITE_MIN_YEAR.
export const SATELLITE_MIN_YEAR = 2020
export type SatelliteYear = 'latest' | number
export const SATELLITE_YEAR_DEFAULT: SatelliteYear = 'latest'

// ─── Terrain markers / waypoints (ticket R2) ─────────────────────────────────
// Mirrors backend/shared/constants.py. The backend REJECTS (does not clamp) a
// marker list longer than MAX_MARKERS or a size_mm outside the diameter range,
// so the builder caps the list at MAX_MARKERS and clamps size_mm into
// [MARKER_MIN_DIAMETER_MM, MARKER_MAX_DIAMETER_MM] before sending.
export const MAX_MARKERS = 20
export const MARKER_DEFAULT_DIAMETER_MM = 3.0
export const MARKER_MIN_DIAMETER_MM = 1.0
export const MARKER_MAX_DIAMETER_MM = 10.0
/** Default hex for a freshly-placed marker (a warm, high-contrast dot). */
export const MARKER_DEFAULT_COLOR = '#E4572E'

export const ELEVATION_BAND_SWATCHES: readonly string[] = COLOR_SWATCHES.terrain
export const DEFAULT_ELEVATION_BAND_COLORS: readonly string[] = [
  '#8B7355',
  '#D2B48C',
  '#5F8367',
  '#a0402d',
  '#E5E5E5',
]

// ─── Paint-layer defaults ────────────────────────────────────────────────────
/** A printer profile card. Values mirror the authoritative backend profiles.
 *  - `vendor`   groups the picker under a brand header.
 *  - `maxColors` is the filament/color ceiling — Bambu AMS = 16, Snapmaker U1 = 4.
 *  - `nozzle`   is the nozzle count (dual-nozzle machines = 2), shown in the spec
 *               line where it's decision-relevant.
 *  - `toolheads` is a tool-changer's physical head count, kept distinct from
 *               `nozzle` (active-at-once count) so changer machines are never
 *               mislabeled by nozzle count alone: the Snapmaker U1's SnapSwap
 *               changer (toolheads: 4, nozzle: 1) and the Bambu H2C's Vortek
 *               changer (toolheads: 6, nozzle: 2 active heads). */
export interface Printer {
  readonly key: string
  readonly name: string
  readonly plate: string
  readonly vendor: 'bambu' | 'snapmaker'
  readonly maxColors: number
  readonly nozzle?: number
  readonly toolheads?: number
  /** Numeric bed (R7 E5), mirrored from backend/pipeline/bambu/printers.py and
   *  parity-tested (printerFit.test.ts). `plateW × plateH` is what the fit
   *  verdict measures (R3 `fits_printers`); `usableXMin..usableXMax` is the
   *  dual-nozzle window the export centres a single-plate model in. */
  readonly plateW: number
  readonly plateH: number
  readonly usableXMin: number
  readonly usableXMax: number
}

export const PRINTERS = [
  {
    key: 'a1_mini',
    name: 'A1 Mini',
    plate: '180 × 180 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 180,
    plateH: 180,
    usableXMin: 0,
    usableXMax: 180,
  },
  {
    key: 'a1',
    name: 'A1',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 256,
    plateH: 256,
    usableXMin: 0,
    usableXMax: 256,
  },
  {
    key: 'p1p',
    name: 'P1P',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 256,
    plateH: 256,
    usableXMin: 0,
    usableXMax: 256,
  },
  {
    key: 'p1s',
    name: 'P1S',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 256,
    plateH: 256,
    usableXMin: 0,
    usableXMax: 256,
  },
  {
    key: 'p2s',
    name: 'P2S',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 256,
    plateH: 256,
    usableXMin: 0,
    usableXMax: 256,
  },
  {
    key: 'x1c',
    name: 'X1 Carbon',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 256,
    plateH: 256,
    usableXMin: 0,
    usableXMax: 256,
  },
  {
    key: 'x2d',
    name: 'X2D',
    plate: '256 × 256 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 2,
    plateW: 256,
    plateH: 256,
    usableXMin: 20.5,
    usableXMax: 256,
  },
  {
    key: 'h2c',
    name: 'H2C',
    plate: '330 × 320 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 2,
    toolheads: 6,
    plateW: 330,
    plateH: 320,
    usableXMin: 25,
    usableXMax: 325,
  },
  {
    key: 'h2d',
    name: 'H2D',
    plate: '350 × 320 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 2,
    plateW: 350,
    plateH: 320,
    usableXMin: 25,
    usableXMax: 325,
  },
  {
    key: 'h2s',
    name: 'H2S',
    plate: '340 × 320 mm',
    vendor: 'bambu',
    maxColors: 16,
    nozzle: 1,
    plateW: 340,
    plateH: 320,
    usableXMin: 0,
    usableXMax: 340,
  },
  {
    key: 'u1',
    name: 'Snapmaker U1',
    plate: '270 × 270 mm',
    vendor: 'snapmaker',
    maxColors: 4,
    nozzle: 1,
    toolheads: 4,
    plateW: 270.5,
    plateH: 270.5,
    usableXMin: 0,
    usableXMax: 270.5,
  },
] as const satisfies readonly Printer[]

export type PrinterKey = (typeof PRINTERS)[number]['key']

/** Brand headers for the printer picker, in stable display order (Bambu first). */
export const PRINTER_VENDORS = [
  { id: 'bambu', label: 'Bambu Lab' },
  { id: 'snapmaker', label: 'Snapmaker' },
] as const satisfies readonly { id: Printer['vendor']; label: string }[]

export const MAX_BAMBU_SUBJOBS_PER_PARENT = 8

/** The default number of colors most printer setups handle without extra AMS
 * units or toolhead swaps. Used both for the picker's color-ceiling spec line
 * and for the export modal's soft, non-blocking over-limit warning. Folds in
 * backlog story E1 ("AMS-aware color limit / warning"): the warning counts the
 * TOTAL effective colors (terrain bands PLUS the lake/trail), not just the
 * terrain bands, so a "4 terrain + lake = 5" export is surfaced. */
export const COLOR_WARN_THRESHOLD = 4

/** Max length for a user-facing model / job name (the editable `trail_name`).
 * Enforced on every name INPUT and on the outgoing create-config schema so a new
 * name can never exceed it. NOT applied to response schemas — legacy stored names
 * (auto-derived from lake joins) can be thousands of chars, and a `.max()` on a
 * response would make parsing throw and break the page; those overlong names are
 * handled by TRUNCATING the display instead. */
export const MAX_MODEL_NAME_LENGTH = 100
